import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowLeft, ExternalLink, FileSpreadsheet } from "lucide-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { LinePlacementLegend } from "@/features/power_projects/components/LinePlacementLegend";
import { MdcpContractorMap } from "@/features/power_projects/components/MdcpContractorMap";
import { drawPlacedLines } from "@/features/power_projects/components/mdcpMap";
import { downloadContractorParcels, getContractorProjectMap } from "../api";
import {
  DECLARED_STATUS_LABEL,
  escapeHtml,
  monthLabel,
  PARCEL_STYLE,
  PLAIN_PARCEL_STYLE,
  ROUTE_STYLE,
  SCOPE_LABEL,
  STATUS_LABEL,
} from "../components/mapStyle";
import type { ContractorRoute, ContractorWpwParcel, MapScope, WpwSection } from "../types";

/** Mapa jednego projektu w panelu podwykonawcy. Profil WPW: trasa + działki prywatne
 *  kolorowane statusem własnych deklaracji, dymki z właścicielami (tylko przy zleconym),
 *  eksport wykazu. Profil MDCP: istniejąca mapa geodety (punkty zdjęciowe + upload).
 *  Projekt „planowany": geometria i liczniki, bez właścicieli i bez eksportu. */
export function ContractorProjectMapPage() {
  // previewContractorId ustawione = tryb podglądu „oczami podwykonawcy" (wewnętrzni).
  const { connectionId = "", previewContractorId } = useParams<{
    connectionId: string;
    previewContractorId?: string;
  }>();
  const { data: result, isLoading, isError } = useQuery({
    queryKey: ["contractor-map", "project", connectionId, previewContractorId ?? "self"],
    queryFn: () => getContractorProjectMap(connectionId, previewContractorId),
    enabled: Boolean(connectionId),
    retry: false,
    staleTime: 30_000,
  });
  const data = result?.project;

  const availableScopes = useMemo(() => {
    const scopes: MapScope[] = [];
    if (data?.wpw) scopes.push("wpw");
    if (data?.mdcp) scopes.push("mdcp_geodezja");
    return scopes;
  }, [data]);
  const [scope, setScope] = useState<MapScope | null>(null);
  const activeScope = scope && availableScopes.includes(scope) ? scope : availableScopes[0] ?? null;

  const [exportError, setExportError] = useState<string | null>(null);
  const exportMutation = useMutation({
    // scope jedzie jawnie w argumencie mutacji — bez rzutowania null-a na MapScope.
    mutationFn: (v: { fileFormat: "xlsx" | "csv"; scope: MapScope }) =>
      downloadContractorParcels(connectionId, v.scope, v.fileFormat, previewContractorId),
    onMutate: () => setExportError(null),
    onError: () => setExportError("Nie udało się pobrać wykazu — spróbuj ponownie."),
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (isError || !data) {
    return (
      <div className="p-6">
        <BackLink previewContractorId={previewContractorId} />
        <p className="mt-3 text-sm text-[var(--color-primary)]">Nie znaleziono projektu.</p>
      </div>
    );
  }

  const section = activeScope === "wpw" ? data.wpw : activeScope === "mdcp_geodezja" ? data.mdcp : null;
  const planned = section?.state === "planned";
  const exportLabel =
    activeScope === "wpw" ? "Pobierz działki z właścicielami" : "Pobierz wykaz działek";

  return (
    <div className="p-4 md:p-6">
      <BackLink previewContractorId={previewContractorId} />
      {previewContractorId && (
        <div className="mt-2 border border-[var(--color-border)] border-l-4 border-l-[var(--color-primary)] bg-white px-3 py-2 text-sm dark:bg-gray-900">
          <span className="font-mono text-[11px] uppercase tracking-wider text-[var(--color-primary)]">
            Podgląd
          </span>{" "}
          — dokładnie to widzi firma <strong>{result?.previewName ?? "…"}</strong> na mapie
          tego projektu (te same dane, ten sam eksport).
        </div>
      )}
      <div className="mt-2 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold">{data.name}</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {[data.commune && `gm. ${data.commune}`, data.county && `pow. ${data.county}`]
              .filter(Boolean)
              .join(", ")}
            {section && section.orders.length > 0 && (
              <> · zamówienie {section.orders.map((o) => o.order_number).join(", ")}</>
            )}
          </p>
        </div>
        {section && (
          <div className="flex flex-wrap items-center gap-2">
            {section.orders.map((order) => (
              <Link
                key={order.id}
                to={
                  previewContractorId
                    ? `/orders/${order.id}`
                    : `/contractor/orders/${order.id}`
                }
                className="inline-flex items-center gap-1 border border-[var(--color-border)] px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] dark:text-gray-300"
                title={`Przejdź do zamówienia ${order.order_number}`}
              >
                <ExternalLink size={13} /> Zamówienie {order.order_number}
              </Link>
            ))}
            <Button
              size="sm"
              onClick={() => activeScope && exportMutation.mutate({ fileFormat: "xlsx", scope: activeScope })}
              disabled={planned || exportMutation.isPending}
              loading={exportMutation.isPending}
              title={
                planned
                  ? "Eksport będzie dostępny po otrzymaniu zamówienia"
                  : "Wykaz działek jako arkusz Excel"
              }
            >
              <FileSpreadsheet size={13} /> {exportLabel} (XLSX)
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => activeScope && exportMutation.mutate({ fileFormat: "csv", scope: activeScope })}
              disabled={planned || exportMutation.isPending}
              title={
                planned ? "Eksport będzie dostępny po otrzymaniu zamówienia" : "Ten sam wykaz jako CSV"
              }
            >
              CSV
            </Button>
          </div>
        )}
      </div>
      {exportError && (
        <p className="mt-2 text-[11px] font-mono text-[var(--color-primary)]">{exportError}</p>
      )}

      {availableScopes.length > 1 && (
        <div className="mt-3 flex gap-1">
          {availableScopes.map((s) => (
            <button
              key={s}
              onClick={() => setScope(s)}
              className={
                s === activeScope
                  ? "border border-[var(--color-primary)] bg-[var(--color-primary)] px-3 py-1 text-xs font-semibold text-white"
                  : "border border-[var(--color-border)] px-3 py-1 text-xs text-gray-600 hover:border-[var(--color-primary)] dark:text-gray-300"
              }
            >
              {SCOPE_LABEL[s]}
            </button>
          ))}
        </div>
      )}

      {planned && (
        <div className="mt-3 border border-[var(--color-border)] border-l-4 border-l-[var(--color-primary)] bg-[var(--color-surface,rgba(0,0,0,0.03))] p-3 text-sm dark:bg-gray-900">
          <span className="font-semibold text-[var(--color-primary)]">Planowane do zlecenia</span>
          {section?.expected_month && (
            <> · przewidywany termin: <strong>{monthLabel(section.expected_month)}</strong></>
          )}
          <span className="text-gray-600 dark:text-gray-300">
            {" "}
            · dane właścicieli i eksport będą dostępne po otrzymaniu zamówienia
          </span>
        </div>
      )}

      {activeScope === "wpw" && data.wpw && (
        <WpwMap
          key={`wpw-${data.wpw.state}`}
          section={data.wpw}
          route={data.route}
        />
      )}

      {activeScope === "mdcp_geodezja" && data.mdcp && (
        // Zamówienie bez rozstawionych punktów zdjęciowych: MdcpContractorMap
        // renderuje null — geodeta i tak musi widzieć trasę i działki (fallback).
        data.mdcp.state === "ordered" &&
        data.mdcp.orders.length > 0 &&
        (data.mdcp.counters.points_count ?? 0) > 0 ? (
          <div className="mt-3">
            <MdcpContractorMap
              orderId={data.mdcp.orders[0].id}
              readOnly={Boolean(previewContractorId)}
            />
          </div>
        ) : (
          <PlainMap
            key={`mdcp-${data.mdcp.state}`}
            parcels={data.mdcp.parcels}
            route={data.route}
          />
        )
      )}
    </div>
  );
}

function BackLink({ previewContractorId }: { previewContractorId?: string }) {
  return (
    <Link
      to={
        previewContractorId
          ? `/power-projects/contractor-preview/${previewContractorId}`
          : "/contractor/map"
      }
      className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-[var(--color-primary)] dark:text-gray-400"
    >
      <ArrowLeft size={13} /> Wróć do mapy zamówień
    </Link>
  );
}

function useOsmMap(containerRef: React.RefObject<HTMLDivElement | null>) {
  const mapRef = useRef<L.Map | null>(null);
  const layersRef = useRef<L.LayerGroup | null>(null);
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = L.map(containerRef.current, {
      center: [52, 19],
      zoom: 6,
      attributionControl: false,
    });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 21 }).addTo(map);
    layersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layersRef.current = null;
    };
  }, [containerRef]);
  return { mapRef, layersRef };
}

// Route: per-segment line kind (solid = kabel w ziemi, dashed = napowietrzna; red build /
// blue demolition) when the version has typed layers, the flat route otherwise. A dimmed
// (planned-only) project stays a flat grey line — the kind is a detail of an ordered job.
function drawRoute(
  layers: L.LayerGroup,
  route: ContractorRoute | null,
  bounds: L.LatLngBounds,
  dim = false,
) {
  if (!dim) {
    const placed = drawPlacedLines(layers, route?.typed_lines);
    if (placed) {
      bounds.extend(placed);
      return;
    }
  }
  if (!route?.geometry) return;
  const layer = L.geoJSON(route.geometry as never, {
    style: dim ? { ...ROUTE_STYLE, color: "#6b7280" } : ROUTE_STYLE,
    interactive: false,
  });
  layer.addTo(layers);
  bounds.extend(layer.getBounds());
}

function ownerPopupHtml(parcel: ContractorWpwParcel): string {
  const head =
    `<div style="font-weight:600">${escapeHtml(parcel.short_number || parcel.teryt)}` +
    (parcel.obreb ? ` · obr. ${escapeHtml(parcel.obreb)}` : "") +
    `</div>` +
    (parcel.address ? `<div style="color:#555">${escapeHtml(parcel.address)}</div>` : "") +
    (parcel.status
      ? `<div style="margin-top:2px">Status: <b>${escapeHtml(STATUS_LABEL[parcel.status])}</b></div>`
      : "");
  const owners = (parcel.owners ?? [])
    .map(
      (owner) =>
        `<li style="margin-top:3px"><b>${escapeHtml(owner.full_name)}</b>` +
        (owner.share ? ` (udział ${escapeHtml(owner.share)})` : "") +
        (owner.address ? `<br>${escapeHtml(owner.address)}` : "") +
        `<br><span style="color:#555">${escapeHtml(
          DECLARED_STATUS_LABEL[owner.declared_status] ?? owner.declared_status,
        )}</span></li>`,
    )
    .join("");
  return (
    head +
    (owners
      ? `<ul style="margin:4px 0 0;padding-left:16px">${owners}</ul>`
      : parcel.owners
        ? `<div style="color:#555;margin-top:2px">Brak właścicieli w zakresie zamówienia.</div>`
        : "")
  );
}

function WpwMap({
  section,
  route,
}: {
  section: WpwSection;
  route: ContractorRoute | null;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { mapRef, layersRef } = useOsmMap(containerRef);
  const ordered = section.state === "ordered";

  useEffect(() => {
    const map = mapRef.current;
    const layers = layersRef.current;
    if (!map || !layers) return;
    layers.clearLayers();
    const bounds = L.latLngBounds([]);
    for (const parcel of section.parcels) {
      if (!parcel.geometry) continue;
      const style =
        ordered && parcel.status ? PARCEL_STYLE[parcel.status] : PLAIN_PARCEL_STYLE;
      const layer = L.geoJSON(parcel.geometry as never, { style });
      layer.bindPopup(ownerPopupHtml(parcel), { maxWidth: 300 });
      layer.addTo(layers);
      bounds.extend(layer.getBounds());
    }
    drawRoute(layers, route, bounds, !ordered);
    const timer = window.setTimeout(() => {
      map.invalidateSize();
      if (bounds.isValid()) map.fitBounds(bounds.pad(0.15));
    }, 80);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, route, ordered]);

  return (
    <div className="mt-3">
      {/* `isolate` keeps Leaflet's z-indexes inside the map box, so the notifications
          panel and modals (z-50) paint above the map — same as MapTab. */}
      <div className="relative isolate z-0 overflow-hidden border border-[var(--color-border)]">
        <div ref={containerRef} className="h-[30rem] w-full" />
      </div>
      <div className="mt-2 flex flex-wrap gap-4 text-[11px] text-gray-600 dark:text-gray-300">
        {ordered ? (
          <>
            <span>
              <i className="mr-1 inline-block h-3 w-3 border border-gray-500 bg-white align-[-1px]" />
              do pozyskania
            </span>
            <span>
              <i className="mr-1 inline-block h-3 w-3 border border-[#1f7a3d] bg-[#4caf7d]/50 align-[-1px]" />
              zgoda pozyskana
            </span>
            <span>
              <i className="mr-1 inline-block h-3 w-3 border border-[#b45309] bg-[#f59e0b]/50 align-[-1px]" />
              problem / odmowa
            </span>
          </>
        ) : (
          <span>
            <i className="mr-1 inline-block h-3 w-3 border border-gray-500 bg-gray-400/30 align-[-1px]" />
            działka wymagająca zgody (bez statusów)
          </span>
        )}
        <span>
          <i className="mr-1 inline-block h-1 w-4 bg-[#c8102e] align-[2px]" />
          trasa projektowana
        </span>
        <span className="ml-auto">
          {section.counters.parcels_required ?? 0} działek ·{" "}
          {section.counters.owners_total ?? 0} właścicieli
          {ordered && <> · {section.counters.owners_acquired ?? 0} pozyskanych</>}
        </span>
      </div>
      {/* Rodzaj linii per odcinek (kablowa/napowietrzna) — rysowany tylko przy żywym
          zamówieniu; projekt „planowany" ma płaską szarą trasę. */}
      {ordered && route?.typed_lines && (
        <LinePlacementLegend summary={route.line_placement} className="mt-1" />
      )}
    </div>
  );
}

function PlainMap({
  parcels,
  route,
}: {
  parcels: { id: string; teryt: string; short_number: string; geometry: GeoJSON.Geometry | null }[];
  route: ContractorRoute | null;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { mapRef, layersRef } = useOsmMap(containerRef);

  useEffect(() => {
    const map = mapRef.current;
    const layers = layersRef.current;
    if (!map || !layers) return;
    layers.clearLayers();
    const bounds = L.latLngBounds([]);
    for (const parcel of parcels) {
      if (!parcel.geometry) continue;
      const layer = L.geoJSON(parcel.geometry as never, { style: PLAIN_PARCEL_STYLE });
      layer.bindPopup(escapeHtml(parcel.short_number || parcel.teryt));
      layer.addTo(layers);
      bounds.extend(layer.getBounds());
    }
    drawRoute(layers, route, bounds, true);
    const timer = window.setTimeout(() => {
      map.invalidateSize();
      if (bounds.isValid()) map.fitBounds(bounds.pad(0.15));
    }, 80);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcels, route]);

  return (
    <div className="mt-3 space-y-1">
      {/* `isolate` keeps Leaflet's z-indexes inside the map box, so the notifications
          panel and modals (z-50) paint above the map — same as MapTab. */}
      <div className="relative isolate z-0 overflow-hidden border border-[var(--color-border)]">
        <div ref={containerRef} className="h-[30rem] w-full" />
      </div>
      {route?.typed_lines && <LinePlacementLegend summary={route.line_placement} />}
    </div>
  );
}
